import { useRef } from "react";
import { QRCodeCanvas, QRCodeSVG } from "qrcode.react";

interface QRCodeProps {
	value: string;
	/** Display size in px. */
	size?: number;
	/** Filename stem for downloads (without extension). */
	filename?: string;
}

/**
 * Renders a QR code client-side (canvas on screen, hidden SVG for export) with
 * PNG + SVG downloads. No server round-trip, no new route, no CSP change
 * (`script-src 'self'` permits canvas + toDataURL + SVG serialization).
 */
export function QRCode({
	value,
	size = 200,
	filename = "abe-lt",
}: QRCodeProps) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const svgRef = useRef<SVGSVGElement>(null);

	async function downloadPng() {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const url = canvas.toDataURL("image/png");
		const a = document.createElement("a");
		a.href = url;
		a.download = `${filename}.png`;
		a.click();
	}

	function downloadSvg() {
		const svg = svgRef.current;
		if (!svg) return;
		const xml = new XMLSerializer().serializeToString(svg);
		const blob = new Blob([xml], { type: "image/svg+xml" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `${filename}.svg`;
		a.click();
		URL.revokeObjectURL(url);
	}

	return (
		<div className="flex flex-col items-center gap-3">
			<div
				className="rounded-lg bg-white p-3"
				style={{ width: size + 24, height: size + 24 }}
			>
				<QRCodeCanvas
					ref={canvasRef}
					value={value}
					size={size}
					level="M"
					marginSize={1}
					aria-label={`QR code for ${value}`}
				/>
			</div>
			{/* Hidden SVG used only for the SVG download. */}
			<div className="hidden" aria-hidden="true">
				<QRCodeSVG
					ref={svgRef}
					value={value}
					size={512}
					level="M"
					marginSize={1}
				/>
			</div>
			<div className="flex gap-2">
				<button
					type="button"
					className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-surface-raised"
					onClick={() => void downloadPng()}
				>
					Download PNG
				</button>
				<button
					type="button"
					className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-surface-raised"
					onClick={downloadSvg}
				>
					Download SVG
				</button>
			</div>
		</div>
	);
}
